/**
 * Cart Property Enricher
 *
 * This script enriches cart items with delivery properties when they're added by
 * third-party apps (like B2B Bulk Order / CSV Upload app) that don't include these properties.
 *
 * Optimized for performance:
 * - Uses debouncing to prevent rapid-fire API calls
 * - Batches updates using /cart/update.js where possible
 * - Polls at longer intervals (5 seconds)
 * - Caches product data to reduce API calls
 */

// Legacy English key, used to detect already-enriched items added before the
// localized property name was introduced. New writes always use the active
// locale's label (e.g. "_Levering" in NL).
//
// The leading underscore is intentional: Shopify hides line item properties
// whose key starts with "_" from the standard checkout (and customer order
// emails / status page). We still render the property in this theme's own
// cart UI by special-casing this key — see cart-line-item-properties.liquid
// and the inline cart iterations.
const LEGACY_DELIVERY_KEY = "_Delivery";

// Active, customer-visible delivery property key. No leading underscore, so it
// shows on the line item in checkout, customer order emails and the order
// status page — exactly what we want for the delivery message.
const ACTIVE_DELIVERY_KEY = "Levering";

// All known delivery property keys across locales. Used to clean up any stale
// variant from the cart so a line item never carries duplicates.
const DELIVERY_KEY_LOCALES = [
  "Delivery",
  "Levering",
  "Lieferung",
  "Livraison",
  "Consegna",
  "Entrega",
  "Doručení",
  "Leverans",
  "配送",
  "送货",
  "送貨",
  "Giao hàng",
];

// Both the un-prefixed and underscore-prefixed forms of every locale key, so a
// switch from the old hidden "_Levering" to the visible "Levering" cleans up
// the old key instead of leaving both on the item.
const LEGACY_UNPREFIXED_DELIVERY_KEYS = DELIVERY_KEY_LOCALES;
const LEGACY_PREFIXED_DELIVERY_KEYS = DELIVERY_KEY_LOCALES.map(
  (key) => `_${key}`,
);
const ALL_DELIVERY_KEYS = [
  ...LEGACY_PREFIXED_DELIVERY_KEYS,
  ...LEGACY_UNPREFIXED_DELIVERY_KEYS,
];

// Hidden line item property holding the computed delivery date. The leading
// underscore means Shopify hides it from the customer in checkout/cart while
// it stays visible to the merchant in the order admin and via the API.
const DELIVERY_DATE_KEY = "_DeliveryDate";

class CartPropertyEnricher {
  constructor() {
    this.enrichingItems = new Set();
    this.productDataCache = new Map();
    this.inflightProductRequests = new Map();
    this.lastCartItemCount = 0;
    this.isProcessing = false;
    this.pendingEnrichment = false;
    this.debounceTimer = null;
    this.enrichmentScheduled = false;
    this.enrichDebounceMs = 200;
    this.cartChangeConcurrency = 3;
    this.pollIntervalMs = 5000;
    this.cartPagePollIntervalMs = 2000;
    this.cartMutationInFlight = 0;
    this.pendingSectionRefreshTimer = null;
    this.pendingSectionRefreshInFlight = false;
    this.pendingSectionRefreshAttempts = 0;
    this.pendingSectionRefreshMaxAttempts = 20;
    this.pendingSectionRefreshIntervalMs = 750;
    // Concurrency / rate-limit handling. Bulk add via 3rd-party apps can
    // produce dozens of items at once; firing all product/cart fetches in
    // parallel quickly trips Shopify's storefront 429 limits.
    this.productFetchConcurrency = 4;
    this.cartChangeSpacingMs = 60;
    this.rateLimitedUntil = 0;
    this.maxFetchRetries = 3;
    this.defaultRetryBackoffMs = 750;
    this.debugEnabled =
      window.theme?.settings?.debugCartPropertyEnricher !== false;
    this.init();
  }

  debug(message, payload) {
    if (!this.debugEnabled) {
      return;
    }

    if (payload === undefined) {
      console.log(`[CartPropertyEnricher] ${message}`);
      return;
    }

    console.log(`[CartPropertyEnricher] ${message}`, payload);
  }

  init() {
    this.debug("init", {
      themeSettings: window.theme?.settings,
    });

    this.initCartUpdateSubscriber();
    this.initFetchInterceptor();
    this.initXHRInterceptor();
    this.initCartPolling();
    this.initPendingSectionRefresh();

    // Run initial check immediately on page load
    this.runInitialCheck();
  }

  /**
   * Run initial enrichment check on page load
   * This catches items that need enrichment when navigating to cart
   */
  async runInitialCheck() {
    try {
      const cart = await this.fetchCart();
      if (!cart) return;
      this.lastCartItemCount = cart.items?.length || 0;

      const hasCartItems = (cart.items?.length || 0) > 0;

      // Check if any items need enrichment
      const needsEnrichment = cart.items?.some(
        (item) => !this.getItemDeliveryValue(item),
      );

      // Check for duplicates
      const hasDuplicates = this.hasDuplicateVariants(cart.items || []);

      if (hasCartItems || hasDuplicates) {
        this.scheduleEnrichment();
      }

      this.debug("runInitialCheck", {
        itemCount: cart.items?.length || 0,
        needsEnrichment,
        hasDuplicates,
        items: (cart.items || []).map((item) => ({
          key: item.key,
          variantId: item.variant_id,
          quantity: item.quantity,
          delivery: this.getItemDeliveryValue(item),
        })),
      });

      if (needsEnrichment && this.hasPendingDeliveryPlaceholders()) {
        this.startPendingSectionRefresh();
      }
    } catch (error) {
      // Silent fail
    }
  }

  initPendingSectionRefresh() {
    window.addEventListener("cart:enrichment:start", () => {
      this.startPendingSectionRefresh();
    });

    window.addEventListener("cart:enrichment:end", () => {
      this.startPendingSectionRefresh({ immediate: true, force: true });
    });

    document.addEventListener("cart:updated", () => {
      this.startPendingSectionRefresh();
    });

    document.addEventListener("cartdrawer:opened", () => {
      if (this.hasPendingDeliveryPlaceholders()) {
        this.startPendingSectionRefresh();
      }
    });
  }

  hasVisibleCartSections() {
    return Boolean(
      document.getElementById("mini-cart") ||
      document.getElementById("main-cart-items"),
    );
  }

  hasPendingDeliveryPlaceholders() {
    return Boolean(document.querySelector("[data-cart-property-pending]"));
  }

  startPendingSectionRefresh(options = {}) {
    if (!this.hasVisibleCartSections()) {
      return;
    }

    if (
      !options.force &&
      !this.hasPendingDeliveryPlaceholders() &&
      !this.isProcessing &&
      !this.enrichmentScheduled &&
      !this.debounceTimer &&
      !this.isCartMutationInFlight()
    ) {
      return;
    }

    if (options.reset !== false) {
      this.pendingSectionRefreshAttempts = 0;
    }

    if (this.pendingSectionRefreshTimer) {
      clearTimeout(this.pendingSectionRefreshTimer);
    }

    const delay = options.immediate ? 0 : this.pendingSectionRefreshIntervalMs;
    this.pendingSectionRefreshTimer = setTimeout(() => {
      this.pollPendingSectionRefresh();
    }, delay);
  }

  async pollPendingSectionRefresh() {
    this.pendingSectionRefreshTimer = null;

    if (!this.hasVisibleCartSections()) {
      this.pendingSectionRefreshAttempts = 0;
      return;
    }

    if (
      this.isProcessing ||
      this.enrichmentScheduled ||
      this.debounceTimer ||
      this.isCartMutationInFlight() ||
      this.isRateLimited()
    ) {
      this.startPendingSectionRefresh({ reset: false });
      return;
    }

    if (this.pendingSectionRefreshInFlight) {
      this.startPendingSectionRefresh({ reset: false });
      return;
    }

    this.pendingSectionRefreshInFlight = true;

    try {
      const cart = await this.fetchCart();
      if (!cart) {
        this.scheduleNextPendingSectionRefresh();
        return;
      }

      const hasPendingDelivery =
        cart?.items?.some((item) => !this.getItemDeliveryValue(item)) ?? false;

      if (hasPendingDelivery) {
        this.scheduleNextPendingSectionRefresh();
        return;
      }

      const didRefresh = await this.refreshVisibleCartSections();

      if (!didRefresh) {
        this.pendingSectionRefreshAttempts = 0;
        return;
      }

      if (this.hasPendingDeliveryPlaceholders()) {
        this.scheduleNextPendingSectionRefresh();
        return;
      }

      this.pendingSectionRefreshAttempts = 0;
    } catch (error) {
      this.scheduleNextPendingSectionRefresh();
    } finally {
      this.pendingSectionRefreshInFlight = false;
    }
  }

  scheduleNextPendingSectionRefresh() {
    this.pendingSectionRefreshAttempts += 1;

    if (
      this.pendingSectionRefreshAttempts >=
      this.pendingSectionRefreshMaxAttempts
    ) {
      this.pendingSectionRefreshAttempts = 0;
      return;
    }

    this.startPendingSectionRefresh({ reset: false });
  }

  async refreshVisibleCartSections() {
    const refreshTasks = [];
    const miniCart = document.getElementById("mini-cart");
    const mainCartItems = document.getElementById("main-cart-items");
    const mainCartSectionId = mainCartItems?.dataset?.id;

    if (miniCart?.dataset?.url) {
      refreshTasks.push(
        this.refreshSectionContent({
          container: miniCart,
          selector: ".shopify-section",
          url: miniCart.dataset.url,
        }),
      );
    }

    if (mainCartItems && mainCartSectionId) {
      refreshTasks.push(
        this.refreshSectionContent({
          container: mainCartItems,
          selector: ".js-contents",
          url: `${window.location.pathname}?section_id=${encodeURIComponent(mainCartSectionId)}`,
        }),
      );
    }

    if (refreshTasks.length === 0) {
      return false;
    }

    const results = await Promise.all(refreshTasks);
    return results.some(Boolean);
  }

  async refreshSectionContent({ container, selector, url }) {
    try {
      const response = await this.fetchWithRetry(url, {
        headers: {
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      if (!response || !response.ok) {
        return false;
      }

      const html = await response.text();
      const parsedDocument = new DOMParser().parseFromString(html, "text/html");
      const nextContent = parsedDocument.querySelector(selector);
      const currentContent = container.querySelector(selector) || container;

      if (!nextContent || !currentContent) {
        return false;
      }

      currentContent.innerHTML = nextContent.innerHTML;
      return true;
    } catch (error) {
      return false;
    }
  }

  isCartMutationInFlight() {
    return this.cartMutationInFlight > 0;
  }

  beginCartMutation() {
    this.cartMutationInFlight += 1;
  }

  endCartMutation() {
    this.cartMutationInFlight = Math.max(0, this.cartMutationInFlight - 1);
  }

  /**
   * Signal that enrichment has started
   */
  startEnrichmentState() {
    window.dispatchEvent(new CustomEvent("cart:enrichment:start"));
  }

  /**
   * Signal that enrichment has completed
   */
  endEnrichmentState() {
    window.dispatchEvent(new CustomEvent("cart:enrichment:end"));
  }

  /**
   * Debounced enrichment - waits 500ms after last trigger before running
   */
  scheduleEnrichment() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    if (!this.enrichmentScheduled && !this.isProcessing) {
      this.enrichmentScheduled = true;
      this.startEnrichmentState();
    }

    this.debounceTimer = setTimeout(async () => {
      this.debounceTimer = null;
      await this.enrichCart();
    }, this.enrichDebounceMs);
  }

  /**
   * Initialize cart update subscriber
   */
  initCartUpdateSubscriber() {
    if (typeof subscribe === "undefined") {
      return;
    }

    subscribe(PUB_SUB_EVENTS.cartUpdate, async (event) => {
      if (event?.source === "cart-property-enricher") {
        return;
      }
      this.scheduleEnrichment();
    });
  }

  /**
   * Intercept fetch calls to /cart/add.js
   */
  initFetchInterceptor() {
    const originalFetch = window.fetch;
    const enricher = this;

    window.fetch = async function (...args) {
      const [url] = args;
      const response = await originalFetch.apply(this, args);

      if (
        enricher.shouldWatchCartAddUrl(url) &&
        response.ok &&
        !enricher.isProcessing
      ) {
        enricher.scheduleEnrichment();
      }

      return response;
    };
  }

  /**
   * Intercept XMLHttpRequest calls
   */
  initXHRInterceptor() {
    const enricher = this;
    const originalOpen = XMLHttpRequest.prototype.open;
    const originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url, ...rest) {
      this._url = url;
      return originalOpen.apply(this, [method, url, ...rest]);
    };

    XMLHttpRequest.prototype.send = function (body) {
      const xhr = this;

      xhr.addEventListener("load", function () {
        if (
          enricher.shouldWatchCartAddUrl(xhr._url) &&
          xhr.status >= 200 &&
          xhr.status < 300 &&
          !enricher.isProcessing
        ) {
          enricher.scheduleEnrichment();
        }
      });

      return originalSend.apply(this, [body]);
    };
  }

  /**
   * Poll cart every 5 seconds (reduced from 2s)
   */
  initCartPolling() {
    const enricher = this;

    const poll = async () => {
      try {
        if (
          !enricher.isProcessing &&
          !enricher.debounceTimer &&
          !enricher.enrichmentScheduled &&
          !enricher.isCartMutationInFlight() &&
          !enricher.isRateLimited() &&
          document.visibilityState !== "hidden"
        ) {
          const cart = await this.fetchCart();
          if (cart) {
            const currentCount = cart.items?.length || 0;

            if (currentCount !== enricher.lastCartItemCount) {
              enricher.lastCartItemCount = currentCount;
              enricher.scheduleEnrichment();
            }
          }
        }
      } catch (error) {
        // Silent fail
      } finally {
        const nextInterval = this.isCartPage()
          ? this.cartPagePollIntervalMs
          : this.pollIntervalMs;
        setTimeout(poll, nextInterval);
      }
    };

    poll();
  }

  /**
   * Main enrichment method - optimized to minimize API calls
   */
  async enrichCart() {
    if (this.isProcessing) {
      this.pendingEnrichment = true;
      return;
    }

    this.isProcessing = true;
    this.enrichmentScheduled = false;
    let shouldScheduleAfter = false;

    try {
      const cart = await this.fetchCart();
      if (!cart?.items || cart.items.length === 0) return;

      this.lastCartItemCount = cart.items.length;

      // Check for duplicates first
      const hasDuplicates = this.hasDuplicateVariants(cart.items);

      if (hasDuplicates) {
        await this.mergeDuplicateVariants(cart.items);
        // After merge, schedule another enrichment to add delivery properties
        shouldScheduleAfter = true;
        return;
      }

      const itemsToSync = cart.items.filter(
        (item) => !this.enrichingItems.has(item.key),
      );

      if (itemsToSync.length === 0) {
        return;
      }

      // Calculate the expected delivery properties and only update changed items
      const updates = await this.getDeliveryPropertiesForItems(itemsToSync);

      this.debug("enrichCart:computedUpdates", {
        updatesCount: updates.length,
        updates: updates.map((item) => ({
          key: item.key,
          variantId: item.id,
          quantity: item.quantity,
          delivery: this.getItemDeliveryValue(item),
        })),
      });

      if (updates.length > 0) {
        await this.batchUpdateCartItems(updates);
      }
    } catch (error) {
      // Silent fail
    } finally {
      this.isProcessing = false;

      if (this.pendingEnrichment || shouldScheduleAfter) {
        this.pendingEnrichment = false;
        this.scheduleEnrichment();
        return;
      }

      this.endEnrichmentState();
    }
  }

  /**
   * Check if cart has duplicate variants
   */
  hasDuplicateVariants(items) {
    const seen = new Set();
    for (const item of items) {
      const signature = this.getLineItemSignature(item);
      if (seen.has(signature)) return true;
      seen.add(signature);
    }
    return false;
  }

  /**
   * Merge duplicate variants without re-adding line items
   */
  async mergeDuplicateVariants(items) {
    const signatureGroups = new Map();
    for (const item of items) {
      const signature = this.getLineItemSignature(item);
      if (!signatureGroups.has(signature)) {
        signatureGroups.set(signature, []);
      }
      signatureGroups.get(signature).push(item);
    }

    const duplicateGroups = [...signatureGroups.values()].filter(
      (groupItems) => groupItems.length > 1,
    );

    if (duplicateGroups.length === 0) return;

    const mergeOperations = duplicateGroups.map((groupItems) => {
      const representative = groupItems[0];
      return {
        itemsToRemove: groupItems.slice(1),
        quantity: groupItems.reduce((sum, item) => sum + item.quantity, 0),
        representative,
      };
    });

    try {
      this.beginCartMutation();

      for (const operation of mergeOperations) {
        await this.changeCartLineItem({
          id: operation.representative.key,
          properties: operation.representative.properties,
          quantity: operation.quantity,
        });

        for (const item of operation.itemsToRemove) {
          await this.changeCartLineItem({
            id: item.key,
            quantity: 0,
          });
        }
      }
    } catch (error) {
      // Silent fail
    } finally {
      this.endCartMutation();
    }
  }

  /**
   * Get delivery properties for multiple items efficiently
   * Uses parallel fetching to speed up the process
   */
  async getDeliveryPropertiesForItems(items) {
    // Group by product handle to minimize API calls
    const handleGroups = new Map();
    for (const item of items) {
      if (!handleGroups.has(item.handle)) {
        handleGroups.set(item.handle, []);
      }
      handleGroups.get(item.handle).push(item);
    }

    // Fetch all product data with a small concurrency limit so a bulk add
    // (e.g. 50+ products via the Bullock app) does not flood Shopify with
    // parallel storefront requests and trigger 429 responses.
    const handles = [...handleGroups.keys()].filter((h) => h);
    const productDataMap = new Map();
    await this.runWithConcurrency(
      handles,
      this.productFetchConcurrency,
      async (handle) => {
        const data = await this.getProductData(handle);
        productDataMap.set(handle, data);
      },
    );

    const updates = [];
    const deliveryKey = this.getDeliveryPropertyKey();

    // Process all items using the pre-fetched data
    for (const [handle, handleItems] of handleGroups) {
      if (!handle) {
        // No handle, use fallback
        for (const item of handleItems) {
          const delivery = this.getFallbackDeliveryMessage();
          const currentDelivery = this.getItemDeliveryValue(item);

          this.debug("getDeliveryPropertiesForItems:fallback", {
            key: item.key,
            variantId: item.variant_id,
            quantity: item.quantity,
            currentDelivery,
            nextDelivery: delivery,
          });

          const deliveryDate = this.getInStockDeliveryDate();

          if (
            currentDelivery !== delivery ||
            !item.properties?.[DELIVERY_DATE_KEY]
          ) {
            updates.push({
              id: item.variant_id,
              key: item.key,
              quantity: item.quantity,
              properties: this.buildPropertiesWithDelivery(
                item.properties,
                deliveryKey,
                delivery,
                deliveryDate,
              ),
            });
          }
        }
        continue;
      }

      const productData = productDataMap.get(handle);

      for (const item of handleItems) {
        const delivery = this.calculateDeliveryMessage(
          productData,
          item.variant_id,
          item.quantity,
        );
        const currentDelivery = this.getItemDeliveryValue(item);
        const deliveryDate = this.calculateDeliveryDate(
          productData,
          item.variant_id,
          item.quantity,
        );

        this.debug("getDeliveryPropertiesForItems:item", {
          key: item.key,
          handle,
          variantId: item.variant_id,
          quantity: item.quantity,
          currentDelivery,
          nextDelivery: delivery,
        });

        if (
          delivery &&
          (currentDelivery !== delivery ||
            !item.properties?.[DELIVERY_DATE_KEY])
        ) {
          updates.push({
            id: item.variant_id,
            key: item.key,
            quantity: item.quantity,
            properties: this.buildPropertiesWithDelivery(
              item.properties,
              deliveryKey,
              delivery,
              deliveryDate,
            ),
          });
        }
      }
    }

    return updates;
  }

  /**
   * Build a properties object with the active delivery key set, while removing
   * any legacy or stale delivery key so the cart never carries duplicates.
   * Also sets the hidden _DeliveryDate property to the supplied date (computed
   * from stock status via calculateDeliveryDate). The date is refreshed
   * whenever this runs, so it follows the delivery message when stock changes.
   */
  buildPropertiesWithDelivery(existing, deliveryKey, deliveryValue, deliveryDate) {
    const next = { ...(existing || {}) };
    // Remove every known delivery key variant (underscore-prefixed + plain,
    // all locales) except the active key, so the line item never carries both
    // the old hidden "_Levering" and the new visible "Levering".
    for (const staleKey of ALL_DELIVERY_KEYS) {
      if (staleKey !== deliveryKey) {
        delete next[staleKey];
      }
    }
    next[deliveryKey] = deliveryValue;
    if (deliveryDate) {
      next[DELIVERY_DATE_KEY] = deliveryDate;
    }
    return next;
  }

  /**
   * Format a Date as an ISO-style YYYY-MM-DD string. This sorts and filters
   * correctly in spreadsheets / BI tools, which is what _DeliveryDate is for.
   */
  formatDate(date) {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  }

  /**
   * In-stock delivery date: next business day (tomorrow; Sat/Sun roll over to
   * Monday). Returns a DD-MM-YYYY string.
   */
  getInStockDeliveryDate() {
    const target = new Date();
    target.setDate(target.getDate() + 1);

    const day = target.getDay();
    if (day === 6) {
      target.setDate(target.getDate() + 2); // Saturday -> Monday
    } else if (day === 0) {
      target.setDate(target.getDate() + 1); // Sunday -> Monday
    }

    return this.formatDate(target);
  }

  /**
   * Backorder delivery date: 4 weeks (28 days) from today, then the next
   * Thursday from that point — or that day itself if it already lands on a
   * Thursday. Returns a DD-MM-YYYY string.
   */
  getBackorderDeliveryDate() {
    const target = new Date();
    target.setDate(target.getDate() + 28);

    const daysUntilThursday = (4 - target.getDay() + 7) % 7;
    target.setDate(target.getDate() + daysUntilThursday);

    return this.formatDate(target);
  }

  /**
   * Compute the delivery date for a variant, mirroring the in-stock /
   * out-of-stock / split branching of calculateDeliveryMessage:
   * - fully in stock  -> next business day (tomorrow)
   * - out of stock    -> backorder date (28 days, then next Thursday)
   * - partially split -> backorder date (the order is only complete once the
   *   backordered part arrives)
   */
  calculateDeliveryDate(productData, variantId, quantity) {
    if (!productData) {
      return this.getInStockDeliveryDate();
    }

    const variant = productData.variants.find((v) => v.id === variantId);
    if (!variant) {
      return this.getInStockDeliveryDate();
    }

    const inventory = variant.inventory_quantity ?? 0;
    const available = Boolean(variant.available);

    // Partial: some in stock, some backordered -> use backorder date
    if (quantity > inventory && inventory > 0) {
      return this.getBackorderDeliveryDate();
    }

    // Out of stock / backorder
    if (!available || inventory === 0 || quantity > inventory) {
      return this.getBackorderDeliveryDate();
    }

    // Fully in stock
    return this.getInStockDeliveryDate();
  }

  /**
   * Public: compute the Delivery line item property for a given product/variant/quantity.
   * Returns { key, value } where key is the active locale's property label
   * (e.g. "Levering" in NL, "Delivery" in EN) so checkout and order emails show
   * the localized name. Used by product-form.js to inject the property into the
   * /cart/add request directly.
   */
  async computeDeliveryProperty({ handle, variantId, quantity }) {
    const numericVariantId = Number(variantId);
    const numericQuantity = Number(quantity) || 1;
    const key = this.getDeliveryPropertyKey();

    if (!handle || !numericVariantId) {
      return { key, value: this.getFallbackDeliveryMessage() };
    }

    const productData = await this.getProductData(handle);
    const value = this.calculateDeliveryMessage(
      productData,
      numericVariantId,
      numericQuantity,
    );
    return { key, value };
  }

  /**
   * Format the split-quantity delivery message ("X stuks: ..., Y stuks: ...")
   * via a locale template so wording can be tuned per language.
   * Lowercases the metafield messages so they read naturally after the colon.
   */
  formatSplitDeliveryMessage({
    inStockQty,
    outOfStockQty,
    unit,
    inStockMsg,
    outOfStockMsg,
  }) {
    const template = this.getSettingValue(
      "delivery_split_message",
      "{{ in_stock_qty }} {{ unit }}: {{ in_stock_msg }}. {{ out_of_stock_qty }} {{ unit }}: {{ out_of_stock_msg }}.",
    );
    const vars = {
      in_stock_qty: inStockQty,
      out_of_stock_qty: outOfStockQty,
      unit,
      in_stock_msg: inStockMsg.toLowerCase(),
      out_of_stock_msg: outOfStockMsg.toLowerCase(),
    };
    return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) =>
      vars[key] !== undefined ? String(vars[key]) : "",
    );
  }

  /**
   * Calculate delivery message for a variant
   */
  calculateDeliveryMessage(productData, variantId, quantity) {
    if (!productData) {
      return this.getFallbackDeliveryMessage();
    }

    const variant = productData.variants.find((v) => v.id === variantId);
    if (!variant) {
      return this.getFallbackDeliveryMessage();
    }

    const inventory = variant.inventory_quantity ?? 0;
    const available = Boolean(variant.available);
    const inStockMsg = this.getInStockDeliveryMessage(productData);
    const outOfStockMsg = this.getOutOfStockDeliveryMessage(productData);
    const quantityUnit =
      productData?.quantityUnit ||
      this.getSettingValue("delivery_quantity_unit", "pcs");
    let deliveryMessage;

    if (quantity > inventory && inventory > 0) {
      const inStockQty = inventory;
      const outOfStockQty = quantity - inventory;
      deliveryMessage = this.formatSplitDeliveryMessage({
        inStockQty,
        outOfStockQty,
        unit: quantityUnit,
        inStockMsg,
        outOfStockMsg,
      });
      this.debug("calculateDeliveryMessage:split", {
        variantId,
        quantity,
        inventory,
        available,
        quantityUnit,
        inStockMsg,
        outOfStockMsg,
        deliveryMessage,
      });
      return deliveryMessage;
    }

    if (!available || inventory === 0 || quantity > inventory) {
      deliveryMessage = outOfStockMsg;
      this.debug("calculateDeliveryMessage:outOfStock", {
        variantId,
        quantity,
        inventory,
        available,
        quantityUnit,
        inStockMsg,
        outOfStockMsg,
        deliveryMessage,
      });
      return deliveryMessage;
    }

    deliveryMessage = inStockMsg;
    this.debug("calculateDeliveryMessage:inStock", {
      variantId,
      quantity,
      inventory,
      available,
      quantityUnit,
      inStockMsg,
      outOfStockMsg,
      deliveryMessage,
    });
    return deliveryMessage;
  }

  /**
   * Get product data with caching
   */
  async getProductData(productHandle) {
    const cached = this.getCachedProductData(productHandle);
    if (cached) return cached;

    if (this.inflightProductRequests.has(productHandle)) {
      return this.inflightProductRequests.get(productHandle);
    }

    const requestPromise = this.fetchProductData(productHandle).finally(() => {
      this.inflightProductRequests.delete(productHandle);
    });

    this.inflightProductRequests.set(productHandle, requestPromise);
    return requestPromise;
  }

  getCachedProductData(productHandle) {
    const cached = this.productDataCache.get(productHandle);
    if (!cached) return null;

    if (cached.expiresAt < Date.now()) {
      this.productDataCache.delete(productHandle);
      return null;
    }

    return cached.data;
  }

  cacheProductData(productHandle, data) {
    const cacheTtlMs = 5 * 60 * 1000;
    this.productDataCache.set(productHandle, {
      data,
      expiresAt: Date.now() + cacheTtlMs,
    });
  }

  async fetchProductData(productHandle) {
    try {
      const response = await this.fetchWithRetry(
        this.buildShopifyUrl(`products/${productHandle}?view=json-data`),
      );

      if (response && response.ok) {
        const text = await response.text();
        const jsonData = this.safeJsonParse(text);

        if (jsonData?.variants?.length > 0) {
          const result = {
            variants: jsonData.variants,
            inStockMessage: jsonData.inStockMessage || "",
            outOfStockMessage: jsonData.outOfStockMessage || "",
            quantityUnit:
              jsonData.quantityUnit ||
              this.getSettingValue("delivery_quantity_unit", "pcs"),
          };

          this.debug("fetchProductData:view=json-data", {
            productHandle,
            inStockMessage: result.inStockMessage,
            outOfStockMessage: result.outOfStockMessage,
            quantityUnit: result.quantityUnit,
            variantsCount: result.variants.length,
          });

          this.cacheProductData(productHandle, result);
          return result;
        }
      }

      const jsonResponse = await this.fetchWithRetry(
        this.buildShopifyUrl(`products/${productHandle}.json`),
      );
      if (jsonResponse && jsonResponse.ok) {
        const data = await jsonResponse.json();
        const result = {
          variants: data.product?.variants || [],
          inStockMessage: this.getInStockDeliveryMessage(),
          outOfStockMessage: this.getOutOfStockDeliveryMessage(),
          quantityUnit: this.getSettingValue("delivery_quantity_unit", "pcs"),
        };

        this.debug("fetchProductData:product-json", {
          productHandle,
          inStockMessage: result.inStockMessage,
          outOfStockMessage: result.outOfStockMessage,
          quantityUnit: result.quantityUnit,
          variantsCount: result.variants.length,
        });

        this.cacheProductData(productHandle, result);
        return result;
      }
    } catch (error) {
      // Silent fail
    }

    return null;
  }

  /**
   * Get theme setting value
   */
  getSettingValue(settingKey, defaultValue) {
    try {
      if (window.theme?.settings?.[settingKey]) {
        return window.theme.settings[settingKey];
      }
    } catch {
      // Silent fail
    }
    return defaultValue;
  }

  /**
   * Fallback delivery message
   */
  getFallbackDeliveryMessage() {
    const fallbackMessage = this.getInStockDeliveryMessage();
    this.debug("getFallbackDeliveryMessage", { fallbackMessage });
    return fallbackMessage;
  }

  getInStockDeliveryMessage(productData = null) {
    if (productData?.inStockMessage) {
      return productData.inStockMessage;
    }

    const inStockMessage = this.getSettingValue(
      "delivery_in_stock_message",
      "Delivery in approx. 1 business day",
    );
    return inStockMessage;
  }

  getOutOfStockDeliveryMessage(productData = null) {
    if (productData?.outOfStockMessage) {
      return productData.outOfStockMessage;
    }

    const outOfStockMessage = this.getSettingValue(
      "delivery_out_of_stock_message",
      "Delivery in approx. 4 weeks",
    );
    return outOfStockMessage;
  }

  /**
   * Active, customer-visible property key — what new writes use ("Levering").
   */
  getDeliveryPropertyKey() {
    return ACTIVE_DELIVERY_KEY;
  }

  /**
   * Read the delivery value off a line item. Only the active key ("Levering")
   * counts as "already enriched". Any item still carrying an old variant
   * ("_Levering", "_Delivery", etc.) returns null, which signals the enricher
   * to rewrite it to the active key and clean up the stale one.
   */
  getItemDeliveryValue(item) {
    if (!item?.properties) return null;
    return item.properties[this.getDeliveryPropertyKey()] ?? null;
  }

  shouldWatchCartAddUrl(url) {
    return typeof url === "string" && url.includes("/cart/add");
  }

  isCartPage() {
    return window.location?.pathname === "/cart";
  }

  getShopifyRoot() {
    return window.Shopify?.routes?.root || "/";
  }

  buildShopifyUrl(path) {
    const root = this.getShopifyRoot();
    if (path.startsWith("/")) {
      return `${root}${path.slice(1)}`;
    }
    return `${root}${path}`;
  }

  safeJsonParse(text) {
    try {
      return JSON.parse(text);
    } catch (error) {
      return null;
    }
  }

  async fetchCart() {
    try {
      const response = await this.fetchWithRetry(
        this.buildShopifyUrl("cart.js"),
      );
      if (!response || !response.ok) return null;
      return await response.json();
    } catch (error) {
      return null;
    }
  }

  async changeCartLineItem(payload) {
    const requestPayload = { ...payload };

    if (
      requestPayload.properties == null ||
      (typeof requestPayload.properties === "object" &&
        Object.keys(requestPayload.properties).length === 0)
    ) {
      delete requestPayload.properties;
    }

    const response = await this.fetchWithRetry(
      this.buildShopifyUrl("cart/change.js"),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify(requestPayload),
      },
    );

    if (!response || !response.ok) {
      throw new Error("Cart change request failed");
    }

    return await response.json();
  }

  async runWithConcurrency(items, limit, task) {
    const queue = [...items];

    const workers = Array.from({ length: limit }, async () => {
      while (queue.length > 0) {
        const item = queue.shift();
        if (!item) break;
        await task(item);
      }
    });

    await Promise.all(workers);
  }

  sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  isRateLimited() {
    return Date.now() < this.rateLimitedUntil;
  }

  markRateLimited(retryAfterMs) {
    const until = Date.now() + Math.max(0, retryAfterMs);
    if (until > this.rateLimitedUntil) {
      this.rateLimitedUntil = until;
    }
  }

  parseRetryAfterMs(response) {
    const header = response?.headers?.get?.("Retry-After");
    if (!header) return null;
    const seconds = Number(header);
    if (!Number.isNaN(seconds) && seconds >= 0) {
      return seconds * 1000;
    }
    const dateMs = Date.parse(header);
    if (!Number.isNaN(dateMs)) {
      return Math.max(0, dateMs - Date.now());
    }
    return null;
  }

  /**
   * fetch wrapper that respects 429 / Retry-After and retries network errors
   * with exponential backoff. Records a global rate-limit cooldown so polling
   * and section refresh skip work until Shopify is happy again.
   */
  async fetchWithRetry(url, options = {}, { maxRetries } = {}) {
    const retries = maxRetries ?? this.maxFetchRetries;

    if (this.isRateLimited()) {
      await this.sleep(this.rateLimitedUntil - Date.now());
    }

    let lastError = null;
    for (let attempt = 0; attempt <= retries; attempt += 1) {
      try {
        const response = await fetch(url, options);
        if (response.status !== 429) {
          return response;
        }

        const retryAfterMs =
          this.parseRetryAfterMs(response) ??
          this.defaultRetryBackoffMs * Math.pow(2, attempt);

        this.markRateLimited(retryAfterMs);
        this.debug("fetchWithRetry:rateLimited", {
          url,
          attempt,
          retryAfterMs,
        });

        if (attempt === retries) {
          return response;
        }

        await this.sleep(retryAfterMs);
      } catch (error) {
        lastError = error;
        if (attempt === retries) break;
        const backoff = this.defaultRetryBackoffMs * Math.pow(2, attempt);
        await this.sleep(backoff);
      }
    }

    if (lastError) throw lastError;
    return null;
  }

  getSellingPlanId(item) {
    return (
      item?.selling_plan_allocation?.selling_plan?.id ||
      item?.selling_plan?.id ||
      null
    );
  }

  hasSellingPlan(item) {
    return Boolean(this.getSellingPlanId(item));
  }

  stripDeliveryProperty(properties) {
    if (!properties) return null;

    const allDeliveryKeys = new Set(ALL_DELIVERY_KEYS);
    const cleaned = {};
    for (const [key, value] of Object.entries(properties)) {
      if (allDeliveryKeys.has(key)) continue;
      if (key === DELIVERY_DATE_KEY) continue;
      if (value === null || value === undefined || value === "") continue;
      cleaned[key] = value;
    }

    return cleaned;
  }

  normalizeProperties(properties) {
    const cleaned = this.stripDeliveryProperty(properties) || {};
    const entries = Object.entries(cleaned)
      .map(([key, value]) => [key, String(value)])
      .sort(([a], [b]) => a.localeCompare(b));

    return JSON.stringify(entries);
  }

  getLineItemSignature(item) {
    const sellingPlanId = this.getSellingPlanId(item) || "none";
    const propertiesSignature = this.normalizeProperties(item.properties);
    return `${item.variant_id}|${sellingPlanId}|${propertiesSignature}`;
  }

  /**
   * Batch update cart items without exposing intermediate cart states
   */
  async batchUpdateCartItems(itemsToUpdate) {
    if (itemsToUpdate.length === 0) return;

    try {
      this.debug("batchUpdateCartItems:start", {
        itemsToUpdate: itemsToUpdate.map((item) => ({
          key: item.key,
          id: item.id,
          quantity: item.quantity,
          delivery: this.getItemDeliveryValue(item),
        })),
      });

      // Mark items as being enriched
      itemsToUpdate.forEach((item) => this.enrichingItems.add(item.key));
      this.beginCartMutation();

      for (let index = 0; index < itemsToUpdate.length; index += 1) {
        const item = itemsToUpdate[index];
        await this.changeCartLineItem({
          id: item.key,
          quantity: item.quantity,
          properties: item.properties,
        });
        if (this.cartChangeSpacingMs > 0 && index < itemsToUpdate.length - 1) {
          await this.sleep(this.cartChangeSpacingMs);
        }
      }

      this.endCartMutation();

      // Unmark items
      setTimeout(() => {
        itemsToUpdate.forEach((item) => this.enrichingItems.delete(item.key));
      }, 500);

      const didRefreshSections = await this.refreshVisibleCartSections();
      this.debug("batchUpdateCartItems:refreshedSections", {
        didRefreshSections,
      });

      if (typeof publish !== "undefined") {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "cart-property-enricher",
        });
      }

      this.debug("batchUpdateCartItems:done", {
        updatedCount: itemsToUpdate.length,
      });
    } catch (error) {
      this.debug("batchUpdateCartItems:error", { error });
      this.endCartMutation();
      itemsToUpdate.forEach((item) => this.enrichingItems.delete(item.key));
    }
  }
}

function bootstrapCartPropertyEnricher() {
  window.cartPropertyEnricher = new CartPropertyEnricher();
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", bootstrapCartPropertyEnricher);
} else {
  bootstrapCartPropertyEnricher();
}